import {
    Box,
    Button,
    CircularProgress,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/SchoolOutlined";
import { OutlinedInput } from "../../components";
import { useIdentityLookup, SiteInfo } from "../../hooks/use-identity-lookup";
import logoImage from "../../assets/SM-02.png";
import { useState, useCallback, useEffect } from "react";
import { colors, shadows, whiteTint } from "../../utils/colors";

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Extract the root/base domain from the current hostname so we can safely
 * prepend a subdomain regardless of whether the user is already on one.
 *
 *   localhost           → localhost
 *   school16.localhost  → localhost
 *   smassets.com        → smassets.com
 *   school16.smassets.com → smassets.com
 */
const getBaseDomain = (): string => {
    const parts = window.location.hostname.split(".");
    if (parts[parts.length - 1] === "localhost") return "localhost";
    return parts.slice(-2).join(".");
};

const buildSubdomainUrl = (subdomain: string): string => {
    const base = getBaseDomain();
    const port = window.location.port ? `:${window.location.port}` : "";
    return `${window.location.protocol}//${subdomain}.${base}${port}`;
};

// ── Site picker step ──────────────────────────────────────────────────────────

const SitePickerStep = ({
    identifier,
    sites,
    onBack,
}: {
    identifier: string;
    sites: SiteInfo[];
    onBack: () => void;
}) => {
    const handleSelect = (site: SiteInfo) => {
        const base = buildSubdomainUrl(site.subdomain);
        window.location.href = `${base}/sign-in?identifier=${encodeURIComponent(identifier)}`;
    };

    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant="body2" color="text.secondary">
                    Signing in as
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap>
                    {identifier}
                </Typography>
            </Box>

            <Divider />

            <Typography variant="body2" color="text.secondary">
                Your account is linked to multiple organizations. Choose one to continue:
            </Typography>

            <List disablePadding>
                {sites.map((site) => (
                    <ListItemButton
                        key={site.subdomain}
                        onClick={() => handleSelect(site)}
                        sx={{ borderRadius: 1, mb: 0.5, border: 1, borderColor: "divider" }}
                    >
                        <SchoolIcon sx={{ mr: 1.5, color: "text.secondary" }} fontSize="small" />
                        <ListItemText
                            primary={site.blog_name}
                            secondary={`${site.subdomain}.${window.location.host}`}
                        />
                    </ListItemButton>
                ))}
            </List>

            <Button variant="text" size="small" onClick={onBack} sx={{ alignSelf: "flex-start" }}>
                ← Back
            </Button>
        </Stack>
    );
};

// ── Main root sign-in view ────────────────────────────────────────────────────

/**
 * Shown on the root / no-subdomain domain instead of the "Invalid site ID" error.
 * Only handles identifier lookup + site selection — password auth happens on
 * the subdomain after the redirect.
 */
export const RootSignInView = () => {
    const { lookup, loading, error, reset } = useIdentityLookup();

    const [step, setStep] = useState<"identifier" | "site-picker">("identifier");
    const [identifier, setIdentifier] = useState("");
    const [sites, setSites] = useState<SiteInfo[]>([]);
    const [touched, setTouched] = useState(false);
    const [lookupError, setLookupError] = useState<string | null>(null);

    // Pre-fill from ?identifier= (shouldn't normally happen on root, but handle gracefully)
    useEffect(() => {
        const param = new URLSearchParams(window.location.search).get("identifier");
        if (param) setIdentifier(param);
    }, []);

    const validationError = touched && !identifier.trim() ? "Username or email is required" : null;

    const handleContinue = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            setTouched(true);
            setLookupError(null);

            if (!identifier.trim()) return;

            const result = await lookup(identifier.trim());
            if (!result) return; // network error already in hook state

            if (!result.found) {
                setLookupError("No account found with that username or email.");
                return;
            }

            if (result.multiple) {
                setSites(result.sites);
                setStep("site-picker");
                return;
            }

            // Single site — construct frontend subdomain URL from the subdomain field
            const base = buildSubdomainUrl(result.site.subdomain);
            window.location.href = `${base}/sign-in?identifier=${encodeURIComponent(identifier.trim())}`;
        },
        [identifier, lookup]
    );

    const handleBack = useCallback(() => {
        setStep("identifier");
        setSites([]);
        reset();
        setLookupError(null);
        setTouched(false);
    }, [reset]);

    const displayError = validationError ?? lookupError ?? error ?? undefined;

    return (
        <Box
            sx={{
                minHeight: "100vh",
                width: "100%",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(160deg, ${colors.navy} 0%, ${colors.navyDeep} 100%)`,
                px: 2,
                py: 4,
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    top: -120,
                    right: -100,
                    width: 340,
                    height: 340,
                    borderRadius: "50%",
                    border: `48px solid ${colors.lime}`,
                    opacity: 0.08,
                    pointerEvents: "none",
                }}
            />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    bottom: -160,
                    left: -120,
                    width: 320,
                    height: 320,
                    borderRadius: "50%",
                    border: `42px solid ${colors.sky}`,
                    opacity: 0.08,
                    pointerEvents: "none",
                }}
            />

            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    maxWidth: 420,
                }}
            >
                <img
                    src={logoImage}
                    alt="Logo"
                    style={{
                        width: "182px",
                        height: "auto",
                        margin: "0 auto",
                        display: "block",
                    }}
                />
                <Paper
                    variant="outlined"
                    sx={{
                        p: { xs: 3, sm: 4 },
                        mt: 3,
                        borderRadius: 4,
                        borderColor: whiteTint(0.08),
                        boxShadow: shadows.heroCard,
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{ color: "text.secondary", mb: 2.5, display: "block" }}
                    >
                        {step === "identifier" ? "Find your organization" : "Choose your organization"}
                    </Typography>

                    {step === "identifier" ? (
                        <Box component="form" onSubmit={handleContinue}>
                            <Stack spacing={2.5}>
                                <OutlinedInput
                                    label="Username or Email"
                                    value={identifier}
                                    onChange={(value) => setIdentifier(value)}
                                    error={displayError}
                                    helperText={displayError}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    size="large"
                                    disabled={loading}
                                    endIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                                    sx={{ py: 1.1, fontWeight: 700 }}
                                >
                                    {loading ? "Looking up..." : "Continue"}
                                </Button>
                            </Stack>
                        </Box>
                    ) : (
                        <SitePickerStep
                            identifier={identifier}
                            sites={sites}
                            onBack={handleBack}
                        />
                    )}
                </Paper>
            </Box>
        </Box>
    );
};
